import { useState } from "react"
import { Button, HStack, Spinner, Text, VStack } from "@chakra-ui/react"
import ErrorAlert from "../../../components/ErrorAlert"
import { useResortSnowReports } from "../../../hooks/useResortSnowReports"
import { extractErrorInfo } from "../../../lib/api/error-utils"
import SnowReportCharts from "../components/SnowReportCharts"
import SnowReportObservationsTable from "../components/SnowReportObservationsTable"
import RunSnowScraperButton from "../components/RunSnowScraperButton"

const historyPeriods = [30, 90, 365]

type Props = {
  resortUid: string
}

export default function ResortSnowReportsTab({ resortUid }: Props) {
  const [days, setDays] = useState(90)
  const { data, isLoading, error } = useResortSnowReports(resortUid, days)
  const errorInfo = error ? extractErrorInfo(error) : null
  const hasObservations = !!data?.Days.length
  const hasIntegration = !!data?.Sources.length

  return (
    <VStack align="stretch" gap="4">
      <HStack justify="space-between" flexWrap="wrap">
        <Text fontWeight="semibold">Snow Reports</Text>
        <HStack>
          {historyPeriods.map((period) => (
            <Button
              key={period}
              size="sm"
              variant={days === period ? "solid" : "ghost"}
              colorPalette="blue"
              color={days === period ? "white" : "gray.300"}
              aria-pressed={days === period}
              onClick={() => setDays(period)}
            >
              {period} days
            </Button>
          ))}
        </HStack>
      </HStack>

      <Text color="gray.400" fontSize="sm">
        Checks every 15 minutes from 5–11 am resort local time until the report’s
        last updated message changes. Charts show confirmed publications; missing
        reports remain gaps.
      </Text>

      {hasIntegration && <RunSnowScraperButton key={resortUid} resortUid={resortUid} />}

      {errorInfo && (
        <ErrorAlert message={errorInfo.message} traceId={errorInfo.traceId} />
      )}
      {isLoading && <Spinner />}
      {data && !hasIntegration && (
        <Text color="gray.400">No snow scraper integration for this resort.</Text>
      )}
      {data && hasIntegration && !hasObservations && (
        <Text color="gray.400">
          No observations recorded yet. The first check establishes a freshness baseline.
        </Text>
      )}
      {data && hasObservations && (
        <>
          <SnowReportCharts rows={data.Days} />
          <SnowReportObservationsTable rows={data.Days} />
        </>
      )}
    </VStack>
  )
}
